import type { LayoutKey, VirtuosaLayout } from './types';

export type ScaleQuality = 'major' | 'minor';

export const SCALE_INTERVALS: Record<ScaleQuality, number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11, 12],
  minor: [0, 2, 3, 5, 7, 8, 10, 12],
};

export interface ScalePlacement {
  keys: LayoutKey[];
  midis: number[];
  totalDistance: number;
}

function distance(a: LayoutKey, b: LayoutKey): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function pickStartingRoot(
  rootSemi: number,
  layout: VirtuosaLayout,
  forceOctave?: number,
): LayoutKey | null {
  const octaves =
    forceOctave !== undefined ? [forceOctave] : [2, 3, 4, 5];

  // Try octaves ascending; pick the first one whose *whole* ascending
  // scale (1 octave) fits in the keyboard range.
  for (const oct of octaves) {
    const rootMidi = rootSemi + 12 * (oct + 1);
    const topMidi = rootMidi + 12;

    if (!layout.byMidi.has(rootMidi) || !layout.byMidi.has(topMidi)) {
      continue;
    }

    const candidates = layout.byMidi.get(rootMidi) ?? [];

    if (candidates.length === 0) {
      continue;
    }

    // Prefer the cell closest to the home row (row 2).
    return candidates.reduce(
      (best, k) =>
        Math.abs(k.row - 2) < Math.abs(best.row - 2) ? k : best,
      candidates[0],
    );
  }

  return null;
}

// Returns the list of octaves for which a one-octave ascending scale fits
// entirely on the keyboard, starting from `rootSemi`.
export function getValidScaleOctaves(
  rootSemi: number,
  layout: VirtuosaLayout,
): number[] {
  const result: number[] = [];

  for (let oct = 2; oct <= 5; oct++) {
    const rootMidi = rootSemi + 12 * (oct + 1);
    const topMidi = rootMidi + 12;

    if (layout.byMidi.has(rootMidi) && layout.byMidi.has(topMidi)) {
      result.push(oct);
    }
  }

  return result;
}

// Greedy nearest-neighbor: from a starting root cell, build the ascending
// scale by always picking the cell (among MIDI candidates for the next
// scale degree) closest to the previous one.
export function placeScale(
  rootSemi: number,
  quality: ScaleQuality,
  layout: VirtuosaLayout,
  startOctave?: number,
): ScalePlacement | null {
  const intervals = SCALE_INTERVALS[quality];
  const start = pickStartingRoot(rootSemi, layout, startOctave);

  if (!start) {
    return null;
  }

  const rootMidi = start.midi;
  const midis = intervals.map((i) => rootMidi + i);

  if (midis.some((m) => !layout.byMidi.has(m))) {
    return null;
  }

  const keys: LayoutKey[] = [start];
  let totalDistance = 0;
  let prev = start;

  for (let i = 1; i < midis.length; i++) {
    const opts = layout.byMidi.get(midis[i]) ?? [];

    if (opts.length === 0) {
      return null;
    }

    const next = opts.reduce((best, k) =>
      distance(prev, k) < distance(prev, best) ? k : best,
    );

    keys.push(next);
    totalDistance += distance(prev, next);
    prev = next;
  }

  return { keys, midis, totalDistance };
}
